import { useApolloClient } from '@apollo/client'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  ShiftManagementBulkCopyUnassignedShiftsDocument,
  ShiftManagementCoverageSectionDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import getMultiFilters from 'modules/core/components/MultiFilters/hooks/utils/getMultiFilters'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  formatCustomFilters,
  getCustomFilters,
} from 'modules/shift_management/components/ShiftManagement/Content/_filters/customFilters'
import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import useApplyFilters from 'modules/shift_management/hooks/useApplyFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import Api from 'modules/shift_management/lib/Api'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import type { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import UnassignedShiftProp from 'modules/shift_management/store/internal/models/UnassignedShift'
import type View from 'modules/shift_management/store/internal/models/View'

import { WizardData } from './utils'

interface Props {
  data: WizardData
  targetView: View
  onClose: () => void
}

export const useCopyPeriodSchedule = ({ data, targetView, onClose }: Props) => {
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchUI] = useUI()
  const { refreshShiftsRows } = useRefreshShiftsRows()
  const apolloClient = useApolloClient()
  const { getFilteredEmployees } = useApplyFilters(shiftsRows)
  const { running: submitting, performAction: handleScheduleSubmit } = useAsyncAction(
    async () => {
      const employeeIds = getFilteredEmployees().map((e) => e.id)
      const { filters: multiFilters, logicOperator } = getMultiFilters(
        LOCAL_STORAGE_KEYS.shiftEmployees
      )

      const custom_filters = getCustomFilters(multiFilters)

      const result = await asyncAction(
        () =>
          Api.copySchedule(data.startDateSchedule, data.destinationStartDate, {
            employee_ids: employeeIds,
            logic_operator: logicOperator.get('logic_operator').value,
            custom_filters: formatCustomFilters(custom_filters),
            days_to_copy: data.daysSchedule,
            include_draft_shifts: true,
          }),
        {
          successFlash: true,
          errorFlash: true,
        }
      )

      if (isSuccessful(result)) {
        // TODO: Actually update the cache properly
        apolloClient.refetchQueries({ include: [ShiftManagementMainPageDocument] })
        const { value: schedule } = result
        const copiedShifts = schedule.shifts
        const copiedRestDays = schedule.rest_days
        const impactedEmployees: number[] = uniq(
          copiedShifts.map((s: ShiftPayload) => s.employee_id)
        )
        const hexCodes = copiedShifts.map((shift: ShiftPayload) => shift.color)
        const uniqHexCodes = uniq(hexCodes)
        const color = uniqHexCodes.map((code) => (code ? nameFromHexCode(code) : null))

        trackAddShiftSubmitEvent({
          view,
          employeeIds: impactedEmployees,
          shiftsCount: copiedShifts.length,
          restDaysCount: copiedRestDays.length,
          shiftType: 'copyPeriod',
          color,
          from: 'copy_period',
          withSearch: '',
          advancedOptions: false,
          extras: { days: data.daysSchedule },
        })

        dispatchUI({ type: 'LOADING' })
        dispatchSchedule({ type: 'RESET_VIEW', view: targetView })
        dispatchPagination({ type: 'RESET_PAGINATION' })

        await refreshShiftsRows(shiftsRows, employeeIds, targetView)
        onClose()
      }
    },
    {
      successMessage: i18n.t('shift_management.actions.copy_shifts_success'),
      errorFlash: false,
    }
  )

  return {
    submitting,
    handleScheduleSubmit,
  }
}

export const useCopyPeriodCoverage = ({ data, targetView, onClose }: Props) => {
  const [, dispatchSchedule] = useSchedule()
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const [bulkCopyUnassignedShifts, { loading }] = useMutation(
    ShiftManagementBulkCopyUnassignedShiftsDocument,
    {
      extractorFn: (x) => x.shiftManagementMutations.bulkCopyShiftmanagementUnassignedShift,
    }
  )

  const handleCoverageSubmit = async () => {
    const result = await bulkCopyUnassignedShifts({
      variables: {
        companyId: currentCompanyId,
        daysToCopy: data.daysCoverage,
        originalStartAt: data.startDateCoverage,
        targetStartAt: data.destinationStartDate,
      },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementCoverageSectionDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.copy_unassigned_shifts_success'),
      })

      const values = handled.data?.unassignedShifts || []
      const unassignedShiftsList = values.map((unassignedShift) =>
        UnassignedShiftProp.buildFromGraphQL(unassignedShift)
      )

      dispatchSchedule({ type: 'RESET_VIEW', view: targetView })
      dispatchUnassignedShifts({
        type: 'ADD_UNASSIGNED_SHIFTS',
        unassignedShifts: unassignedShiftsList,
      })

      onClose()
    }
  }

  return {
    submittingCoverage: loading,
    handleCoverageSubmit,
  }
}
